import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_VideoSizeReducer = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/video-size-reducer',
			name: 'Video Size Reducer — Shrink Files in Seconds | Lift',
			description:
				'Shrink an MP4 or other video file in seconds with Lift while keeping it playable. Pick a level, then download the smaller file. Try Lift now!',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/video-size-reducer#product' },
				{ '@id': 'https://liftapp.ai/video-size-reducer#howto' },
				{ '@id': 'https://liftapp.ai/video-size-reducer#benefits' },
				{ '@id': 'https://liftapp.ai/video-size-reducer#faq' },
				{ '@id': 'https://liftapp.ai/video-size-reducer#why-lift' },
				{ '@id': 'https://liftapp.ai/video-size-reducer#explore-tools' },
				{ '@id': 'https://liftapp.ai/video-size-reducer#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/video-size-reducer#product',
			name: 'Video Size Reducer Online',
			description:
				"Shrink video files to a smaller size while keeping the same format. Pick a level and download the lighter file in seconds. Try Lift's video size reducer now!",
			url: 'https://liftapp.ai/video-size-reducer',
			fileFormat: [
				'video/mp4',
				'video/quicktime',
				'video/x-msvideo',
				'video/webm',
				'video/x-matroska',
				'video/mpeg',
			],
			memoryRequirements: 'Max file size: 500MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/video-size-reducer#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your video',
					text: 'Add an MP4 or other video file from your device to start reducing its size.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Choose a compression level',
					text: 'Pick Low, Medium, or High to balance file size against playback quality.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Shrink your video',
					text: "Lift processes the file and shows how much size it saved once it's done.",
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download or try another file',
					text: 'Save the smaller file, or upload another one to try again.',
				},
			],
		},
		// benefits
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/video-size-reducer#benefits',
			name: 'Who can benefit from the Lift Video Size Reducer?',
			description:
				'Lift Video Size Reducer helps social media creators, students, professionals, and mobile users make videos smaller without a lengthy re-edit.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Social media creators — Fit video uploads under platform size limits without a lengthy re-edit.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Students and professionals — Trim a video down so it clears email and form attachment limits.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'People sharing over messaging apps — A smaller file uploads and delivers faster over any connection.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Anyone low on device storage — Compress older videos to free up space in seconds.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Mobile users — Free up phone storage by compressing videos before saving or sharing them.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Video size reducer tool',
				description: 'Video size reducer — Lift',
			},
		},
		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/video-size-reducer#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is the Lift Video Size Reducer?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "The Lift Video Size Reducer shrinks a video file's size while keeping it in the same format. Choose a size level and download the result in seconds.",
					},
				},
				{
					'@type': 'Question',
					name: 'How does the Lift Video Size Reducer work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload a video, pick a compression level — Low, Medium, or High — and Lift reduces the file size in seconds. Download the smaller version straight from the result screen or try another file.',
					},
				},
				{
					'@type': 'Question',
					name: 'What video formats can I reduce with Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift reduces MP4 and other common video formats and exports the result in the same format you uploaded. No conversion step is needed.',
					},
				},
				{
					'@type': 'Question',
					name: "Will reducing my video's size affect its quality?",
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Higher size-reduction levels create a lighter file but trade off some visual detail. Medium works well for most videos, balancing size and quality.',
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe to upload my videos to Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — Lift processes your videos securely and uses them only to generate your results.',
					},
				},
			],
		},
		// why-lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/video-size-reducer#why-lift',
			name: 'Why Lift Video Size Reducer',
			description:
				'Discover the advantages of Lift Video Size Reducer: high-quality results, intelligent rendering, cross-platform support, and budget-friendly pricing.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description:
						'Available on iOS, Android, and Web for seamless access.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyLift.b11abf6c.webp',
				name: 'Why use Lift Video Size Reducer',
				description: 'Why use Lift Video Size Reducer — Lift.',
			},
		},
		// explore-tools
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/video-size-reducer#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},
		// all-in-one
		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/video-size-reducer#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Video Size Reducer',
					item: 'https://liftapp.ai/video-size-reducer',
				},
			],
		},
	],
};
